<template>
  <v-form
    ref="form"
    lazy-validation
    v-model="formValid"
    v-if="item != null && secretStorages != null"
  >
    <v-alert :value="formError" color="error" class="mb-6">{{ formError }}</v-alert>

    <v-text-field
      v-model="item.name"
      :label="$t('keyName')"
      :rules="[(v) => !!v || $t('name_required')]"
      required
      :disabled="formSaving"
      outlined
      dense
    />

    <v-card
      class="mb-6"
      :color="$vuetify.theme.dark ? '#212121' : 'white'"
      style="background: #8585850f"
    >
      <v-tabs
          fixed-tabs
          v-model="sourceStorageTypeIndex"
      >
        <v-tab
            :disabled="formSaving || !canEditSecrets || isSynced" style="padding: 0">Local</v-tab>
        <v-tab
            :disabled="formSaving || !canEditSecrets || isSynced" style="padding: 0">Storage</v-tab>
        <v-tab :disabled="formSaving || !canEditSecrets || isSynced" style="padding: 0">Env</v-tab>
        <v-tab :disabled="formSaving || !canEditSecrets || isSynced" style="padding: 0">File</v-tab>
      </v-tabs>

      <div class="ml-4 mr-4 mt-6" v-if="sourceStorageType">
        <v-autocomplete
          v-if="supportStorages && sourceStorageType === 'vault'"
          v-model="item.source_storage_id"
          :label="$t('Storage')"
          :items="secretStorages"
          item-value="id"
          item-text="name"
          :disabled="formSaving || !canEditSecrets || isSynced"
          outlined
          dense
          clearable
        />

        <v-text-field
          v-if="supportStorages && sourceStorageType === 'vault' && item.source_storage_id != null"
          v-model="item.source_storage_key"
          :label="$t('Source Key')"
          :disabled="formSaving || !canEditSecrets || isSynced"
          outlined
          dense
        />

        <v-text-field
          v-if="supportStorages && ['env', 'file'].includes(sourceStorageType)"
          v-model="item.source_storage_key"
          :label="
            sourceStorageType === 'env' ? $t('Environment variable name') : $t('Path to the file')
          "
          :rules="[(v) => !!v || $t('type_required')]"
          :disabled="formSaving || !canEditSecrets"
          outlined
          dense
        />
      </div>
    </v-card>

    <v-select
      v-model="item.type"
      :label="$t('type')"
      :rules="[(v) => !!v || !canEditSecrets || $t('type_required')]"
      :items="inventoryTypes"
      item-value="id"
      item-text="name"
      :required="canEditSecrets"
      :disabled="formSaving || !canEditSecrets"
      outlined
      dense
    />

    <v-alert v-if="isReadOnly" type="info" text>Read-only secret storage chosen.</v-alert>

    <v-text-field
      v-model="item.login_password.login"
      :label="$t('usernameOptional')"
      v-if="!isReadOnly && item.type === 'login_password'"
      :disabled="formSaving || !canEditSecrets"
      outlined
      dense
    />

    <v-text-field
      v-model="item.login_password.password"
      :label="$t('password')"
      :rules="[(v) => !!v || !canEditSecrets || $t('password_required')]"
      :class="{ 'masked-secret-input': !showLoginPassword }"
      v-if="!isReadOnly && item.type === 'login_password'"
      :required="canEditSecrets"
      :disabled="formSaving || !canEditSecrets"
      autocomplete="new-password"
      outlined
      dense
    >
      <template #append>
        <ZpIcon
          :name="showLoginPassword ? 'eye' : 'eye-off'"
          :size="18"
          style="cursor:pointer"
          :aria-label="$t(showLoginPassword ? 'hidePassword' : 'showPassword')"
          @click.stop="showLoginPassword = !showLoginPassword"
          @mousedown.prevent
        />
      </template>
    </v-text-field>

    <v-text-field
      v-model="item.ssh.login"
      :label="$t('usernameOptional')"
      v-if="!isReadOnly && item.type === 'ssh'"
      :disabled="formSaving || !canEditSecrets"
      outlined
      dense
    />

    <v-text-field
      v-model="item.ssh.passphrase"
      label="Passphrase (Optional)"
      :class="{ 'masked-secret-input': !showSSHPassphrase }"
      v-if="!isReadOnly && item.type === 'ssh'"
      :disabled="formSaving || !canEditSecrets"
      outlined
      dense
    >
      <template #append>
        <ZpIcon
          :name="showSSHPassphrase ? 'eye' : 'eye-off'"
          :size="18"
          style="cursor:pointer"
          :aria-label="$t(showSSHPassphrase ? 'hidePassword' : 'showPassword')"
          @click.stop="showSSHPassphrase = !showSSHPassphrase"
          @mousedown.prevent
        />
      </template>
    </v-text-field>

    <v-textarea
      outlined
      v-model="item.ssh.private_key"
      :label="$t('privateKey')"
      :disabled="formSaving || !canEditSecrets"
      :rules="[(v) => !canEditSecrets || !!v || $t('private_key_required')]"
      v-if="!isReadOnly && item.type === 'ssh'"
    />

    <div v-if="!isReadOnly && item.type === 'env_vars'">
      <v-textarea
        outlined
        :value="envVarsText"
        @input="onEnvVarsInput"
        :label="$t('keyFormEnvVarsLabel')"
        :placeholder="envVarsPlaceholder"
        :disabled="formSaving || !canEditSecrets"
        :rules="[envVarsLineRule]"
        :hint="$t('keyFormEnvVarsHint')"
        persistent-hint
        rows="6"
      />
    </div>

    <v-checkbox
        v-model="item.override_secret"
        :label="$t('override')"
        v-if="!isNew"
    />

    <v-alert dense text type="info" v-if="item.type === 'none'">
      {{ $t('useThisTypeOfKeyForHttpsRepositoriesAndForPlaybook') }}
    </v-alert>
  </v-form>
</template>
<script>
import ItemFormBase from '@/components/ItemFormBase';

export default {
  mixins: [ItemFormBase],

  props: {
    supportStorages: Boolean,
  },

  data() {
    return {
      showLoginPassword: false,
      showSSHPassphrase: false,
      inventoryTypes: [
        {
          id: 'ssh',
          name: `${this.$t('keyFormSshKey')}`,
        },
        {
          id: 'login_password',
          name: `${this.$t('keyFormLoginPassword')}`,
        },
        {
          id: 'env_vars',
          name: `${this.$t('keyFormEnvVars')}`,
        },
        {
          id: 'none',
          name: `${this.$t('keyFormNone')}`,
        },
      ],
      envVarsText: '',
      secretStorages: null,
      isSynced: false,
    };
  },

  computed: {
    sourceStorageType() {
      return this.item?.source_storage_type;
    },

    sourceStorageTypeIndex: {
      get() {
        return (
          {
            vault: 1,
            env: 2,
            file: 3,
          }[this.item.source_storage_type] || 0
        );
      },
      set(index) {
        this.item = {
          ...this.item,
          source_storage_type: [undefined, 'vault', 'env', 'file'][index],
        };
      },
    },

    canEditSecrets() {
      return this.isNew || this.item.override_secret;
    },

    isReadOnly() {
      if (!this.sourceStorageType) {
        return false;
      }

      if (['env', 'file'].includes(this.sourceStorageType)) {
        return true;
      }

      if (this.item.source_storage_id == null) {
        return false;
      }

      const storage = this.secretStorages.find((s) => s.id === this.item.source_storage_id);
      if (storage == null) {
        return false;
      }

      return storage.readonly;
    },

    envVarsPlaceholder() {
      return 'AWS_ACCESS_KEY_ID=AKIA…\nAWS_SECRET_ACCESS_KEY=…\nVAULT_TOKEN=hvs.…';
    },
  },

  async created() {
    [this.secretStorages] = await Promise.all([this.loadProjectResources('secret_storages')]);
  },

  watch: {
    'item.env_vars': {
      handler(val) {
        if (!this.envVarsText) {
          this.envVarsText = this.formatEnvVars(val?.vars || {});
        }
      },
      immediate: false,
    },
    'item.type': function syncEnvVarsTextOnType(newType) {
      if (newType === 'env_vars' && !this.envVarsText) {
        this.envVarsText = this.formatEnvVars(this.item.env_vars?.vars || {});
      }
    },
  },

  methods: {
    afterLoadData() {
      this.isSynced = JSON.parse(this.item.plain || '{}').dvls_id != null;
      if (this.item?.type === 'env_vars') {
        this.envVarsText = this.formatEnvVars(this.item.env_vars?.vars || {});
      }
    },

    formatEnvVars(vars) {
      return Object.entries(vars || {})
        .map(([k, v]) => `${k}=${v}`)
        .join('\n');
    },

    parseEnvVars(text) {
      const result = {};
      const errors = [];
      (text || '').split('\n').forEach((rawLine, idx) => {
        const line = rawLine.trim();
        if (!line || line.startsWith('#')) return;
        const eq = line.indexOf('=');
        if (eq <= 0) {
          errors.push(`line ${idx + 1}: expected KEY=VALUE`);
          return;
        }
        const k = line.slice(0, eq).trim();
        const v = line.slice(eq + 1);
        if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(k)) {
          errors.push(`line ${idx + 1}: invalid env-var name "${k}"`);
          return;
        }
        result[k] = v;
      });
      return { result, errors };
    },

    onEnvVarsInput(text) {
      this.envVarsText = text;
      const { result } = this.parseEnvVars(text);
      if (!this.item.env_vars) this.item.env_vars = { vars: {} };
      this.item.env_vars.vars = result;
    },

    envVarsLineRule() {
      const { errors } = this.parseEnvVars(this.envVarsText);
      return errors.length === 0 || errors[0];
    },

    getNewItem() {
      return {
        ssh: {},
        login_password: {},
        env_vars: { vars: {} },
      };
    },

    getItemsUrl() {
      return `/api/project/${this.projectId}/keys`;
    },

    getSingleItemUrl() {
      return `/api/project/${this.projectId}/keys/${this.itemId}`;
    },
  },
};
</script>
